import React, { useCallback } from "react";
import { format, Locale } from "date-fns";
import { es, fr, enUS } from "date-fns/locale";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocaleProvider } from "../../locale-provider";
import { msgs } from "../msgs";
import { ConversationSummary } from "../types";
import { ReactComponent as TrashIcon } from "../assets/trash.svg";
import { ReactComponent as DeleteWarningIcon } from "../assets/delete-warning.svg";
import { usePendingConfirmation } from "../hooks/usePendingConfirmation";

export interface ChatHistoryListProps {
  sessions: ConversationSummary[];
  loading: boolean;
  error: string | null;
  disabled: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

function getDateFnsLocale(currentLocale: string) {
  if (currentLocale.startsWith("fr")) {
    return fr;
  }
  if (currentLocale.startsWith("en")) {
    return enUS;
  }
  return es;
}

function formatSessionDate(session: ConversationSummary, dateFnsLocale: Locale): string | null {
  const parsedDate = new Date(session.updatedAt);
  return isNaN(parsedDate.getTime())
    ? null
    : format(parsedDate, "dd/MM/yyyy HH:mm'h'", { locale: dateFnsLocale });
}

interface ChatHistoryRowProps {
  session: ConversationSummary;
  disabled: boolean;
  dateFnsLocale: Locale;
  deleteLabel: string;
  untitledLabel: string;
  onSelect: (id: string) => void;
  onRequestDelete: (session: ConversationSummary) => void;
}

function ChatHistoryRow({
  session,
  disabled,
  dateFnsLocale,
  deleteLabel,
  untitledLabel,
  onSelect,
  onRequestDelete,
}: ChatHistoryRowProps): React.JSX.Element {
  const previewText = session.preview === "" ? untitledLabel : session.preview;
  const formattedDate = formatSessionDate(session, dateFnsLocale);

  const handleRowClick = useCallback(() => {
    if (disabled) return;
    onSelect(session.id);
  }, [disabled, onSelect, session.id]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(session.id);
      }
    },
    [disabled, onSelect, session.id]
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (disabled) return;
      onRequestDelete(session);
    },
    [disabled, onRequestDelete, session]
  );

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        aria-disabled={disabled}
        className={`flex items-center justify-between gap-2 rounded-2xl bg-white px-4 py-3 cursor-pointer transition-opacity focus:outline-none ${
          disabled ? "opacity-40 cursor-not-allowed" : "opacity-100"
        }`}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#212121] truncate mb-1">{previewText}</p>
          {formattedDate !== null && (
            <span className="text-sm text-[#949494] font-weight-light">{formattedDate}</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleDeleteClick}
          aria-disabled={disabled}
          aria-label={deleteLabel}
          title={deleteLabel}
          className={`flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity focus:outline-none ${
            disabled ? "opacity-20 cursor-not-allowed" : ""
          }`}>
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
}

interface DeleteConfirmModalProps {
  session: ConversationSummary;
  formattedDate: string | null;
  untitledLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteConfirmModal({
  session,
  formattedDate,
  untitledLabel,
  onCancel,
  onConfirm,
}: DeleteConfirmModalProps): React.JSX.Element {
  const previewText = session.preview === "" ? untitledLabel : session.preview;

  return (
    <div
      className="absolute inset-0 z-[80] flex items-center justify-center rounded-l-3xl bg-black/50 p-6"
      onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
        className="w-full max-w-[320px] rounded-2xl bg-white p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50">
            <DeleteWarningIcon className="h-4 w-4 text-red-500" aria-hidden />
          </div>
          <p className="m-0 text-base font-semibold text-[#1F2937]">
            <FormattedMessage {...msgs.deleteConfirmTitle} />
          </p>
        </div>
        <p className="mt-2 text-sm text-[#6B7280]">
          <FormattedMessage {...msgs.deleteConfirmDescription} />
        </p>

        <div className="flex-1 min-w-0 mt-4 rounded-xl bg-[#F3F4F6] px-3 py-2.5">
          <p className="text-sm font-semibold text-[#212121] truncate mb-1">{previewText}</p>
          {formattedDate !== null && (
            <span className="text-sm text-[#949494] font-weight-light">{formattedDate}</span>
          )}
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-full border border-gray-300 bg-white py-2 text-sm font-medium text-[#1F2937] transition-colors hover:bg-gray-50 focus:outline-none">
            <FormattedMessage {...msgs.deleteConfirmCancel} />
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-full bg-red-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 focus:outline-none">
            <FormattedMessage {...msgs.deleteConfirmConfirm} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChatHistoryList({
  sessions,
  loading,
  error,
  disabled,
  onSelect,
  onDelete,
}: ChatHistoryListProps): React.JSX.Element {
  const intl = useIntl();
  const localeProvider = useLocaleProvider();
  const dateFnsLocale = getDateFnsLocale(localeProvider.currentLocale);
  const deleteLabel = intl.formatMessage(msgs.buttonDeleteConversation);
  const untitledLabel = intl.formatMessage(msgs.historyUntitled);

  const {
    pending: pendingDelete,
    request: requestDelete,
    cancel: handleCancelDelete,
    confirm: handleConfirmDelete,
  } = usePendingConfirmation<ConversationSummary>(
    useCallback(session => onDelete(session.id), [onDelete])
  );

  const safeSessions = Array.isArray(sessions) ? sessions : [];

  if (loading) {
    // Deliberately not the chat "AI is typing" Loader here — this is just fetching a
    // list of past conversations, not an assistant response, so it gets its own
    // lightweight spinner instead of the avatar + bouncing dots.
    return (
      <div className="flex-1 min-h-0 overflow-y-auto -mx-6 bg-[#F1F5FF] px-6 py-3 flex items-center justify-center">
        <div
          className="w-5 h-5 rounded-full border-2 border-[#C7D2FE] border-t-[#4D54D1] animate-spin"
          role="status"
          aria-label={intl.formatMessage(msgs.historyTitle)}
        />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto -mx-6 bg-[#F1F5FF] px-6 py-3 text-sm text-[#6B7280]">
        <FormattedMessage {...msgs.historyError} />
      </div>
    );
  }

  if (safeSessions.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto -mx-6 bg-[#F1F5FF] px-6 py-3 text-sm text-[#6B7280]">
        <FormattedMessage {...msgs.historyEmpty} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto -mx-6 bg-[#F1F5FF] px-6 py-4">
      <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
        <FormattedMessage {...msgs.historyTitle} />
      </p>
      <ul className="flex flex-col gap-4">
        {safeSessions.map(session => (
          <ChatHistoryRow
            key={session.id}
            session={session}
            disabled={disabled}
            dateFnsLocale={dateFnsLocale}
            deleteLabel={deleteLabel}
            untitledLabel={untitledLabel}
            onSelect={onSelect}
            onRequestDelete={requestDelete}
          />
        ))}
      </ul>

      {pendingDelete !== null && (
        <DeleteConfirmModal
          session={pendingDelete}
          formattedDate={formatSessionDate(pendingDelete, dateFnsLocale)}
          untitledLabel={untitledLabel}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}
